import React, { Suspense, useEffect, useMemo } from 'react';

import { IconX } from '@tabler/icons-react';
import { IconButton, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';

import { signInHref } from '../../utils/common/hrefs';
import useCapabilities from '../../utils/react/hooks/useCapabilities';
import useIsSignedIn from '../../utils/react/hooks/useIsSignedIn';
import ErrorBoundary from '../ErrorBoundary';
import MenuList from '../PageLayout/MenuList';
import { getSettingsSections } from '../PageLayout/settings';
import { isInPaths } from '../PageLayout/utils';
import { SnackbarProvider, SomethingWentWrongMessage } from '../PageLayout';
import Select from '../Select';
import { SkeletonBox, SkeletonGroup } from '../Skeleton';
import { SkeletonSelect } from '../Skeleton/patterns';

import {
  Body,
  Content,
  Header,
  Loading,
  Sidebar,
  SidebarRows,
  StyledModal,
  Surface,
} from './index.styles';

interface SettingsModalProps {
  children: React.ReactNode;
  /** Where closing returns to — the page the modal opened over. */
  onClose: () => void;
}

/** Rows drawn while the caller's capabilities are still resolving. */
const PLACEHOLDER_ROWS = 3;

const TITLE_ID = 'settings-modal-title';

const SectionsSkeleton = () => (
  <SkeletonGroup label='Loading settings sections'>
    <SidebarRows>
      {Array.from({ length: PLACEHOLDER_ROWS }, (_, index) => (
        <SkeletonBox key={index} height={48} radius='control' />
      ))}
    </SidebarRows>
  </SkeletonGroup>
);

/**
 * Settings, as a panel over the page they were opened from. The sections sit
 * beside the page, or above it as a select where two columns do not fit, and
 * hold as the reader goes deeper into a group or a permission.
 */
const SettingsModal = ({ children, onClose }: SettingsModalProps) => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const capabilities = useCapabilities();
  const isSignedIn = useIsSignedIn();

  const sections = useMemo(() => getSettingsSections(capabilities ?? []), [capabilities]);
  const items = useMemo(() => sections.flatMap(section => section.items), [sections]);

  const selected = items.find(item => isInPaths(item.linkedEndpoints, pathname));

  const areCapabilitiesKnown = capabilities !== undefined;

  // One destination is no choice to offer. The column is held while the count is
  // unknown, so it does not appear beside a page already being read.
  const showsSections = !areCapabilitiesKnown || items.length > 1;

  // Account belongs to every caller, so the known sections always render.
  // Placeholders stand in only where they cannot account for the page shown.
  const isLoading = !areCapabilitiesKnown && !selected;

  // The page behind keeps its own location, so it never re-reads the session
  // while the panel opens over it and cannot be what turns the reader away
  useEffect(() => {
    if (isSignedIn === false) {
      navigate(signInHref(window.location.pathname), { replace: true });
    }
  }, [isSignedIn, navigate]);

  const renderSections = () => {
    if (!isMd) {
      return isLoading ? (
        <SkeletonSelect fullWidth label='Loading settings sections' />
      ) : (
        <Select
          label='Settings section'
          fullWidth
          value={selected?.href ?? ''}
          onChange={href => navigate(href)}
          options={items.map(item => ({ value: item.href, label: item.name, icon: item.icon }))}
        />
      );
    }

    return (
      <Sidebar aria-label='Settings sections'>
        {sections.map((section, index) => (
          <MenuList
            key={section.header ?? `section-${index}`}
            header={section.header}
            source={section.items}
            currentPath={pathname}
            expanded
          />
        ))}
        {isLoading && <SectionsSkeleton />}
      </Sidebar>
    );
  };

  // The sections come from groups cached against whoever signed in last, which
  // outlive their session
  if (isSignedIn !== true) {
    return null;
  }

  return (
    <StyledModal open onClose={onClose}>
      <Surface role='dialog' aria-modal='true' aria-labelledby={TITLE_ID}>
        <Header>
          <Typography id={TITLE_ID} variant='h6'>
            Settings
          </Typography>
          <IconButton onClick={onClose} aria-label='Close settings'>
            <IconX />
          </IconButton>
        </Header>
        <Body>
          {showsSections && renderSections()}
          <Content>
            <SnackbarProvider>
              {/* The modal sits outside the page's own boundary and suspense, so
                  a settings chunk or fault stays inside the panel */}
              <ErrorBoundary key={pathname} fallback={<SomethingWentWrongMessage />}>
                <Suspense fallback={<Loading />}>{children}</Suspense>
              </ErrorBoundary>
            </SnackbarProvider>
          </Content>
        </Body>
      </Surface>
    </StyledModal>
  );
};

export default SettingsModal;
